
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { Cargo } from '@/models/Cargo'
import { Transport } from '@/models/Transport'
import TransportType from '@/enums/TransportType'
import { CertificationClaimType } from '@/models/CertificationClaimType'
import ClaimType from '@/enums/ClaimType'
import { Company } from '@/models/Company'
import { Terminal } from '@/models/Terminal'
import moment, { Moment } from 'moment'
import DeliveryMethod from '@/enums/DeliveryMethod'
import DateTimePicker from '@/components/DateTimePicker.vue'
import { CargoType } from '@/models/CargoType'
import DesalinationMethod from '@/enums/DesalinationMethod'
import { BillOfLading } from '@/models/BillOfLading'
import { Vessel } from '@/models/Vessel'
import Testable from '@/mixins/Testable'
import Icon from '@/components/Icon.vue'
import SpecificVesselPicker from '@/components/SpecificVesselPicker.vue'
import UnregisteredVesselCreator from '@/components/UnregisteredVesselCreator.vue'
import LocationSelector, { fetchWhenTerminal } from '@/components/LocationSelector.vue'
import GeoDirection from '@/directions/types/GeoDirection'
import FacilityDirection from '@/directions/types/FacilityDirection'
import { namespace } from 'vuex-class'
import FormValidationResults from '@/components/FormValidationResults.vue'
import User from '@/models/User'
import { TerminalVisit } from '@/models/TerminalVisit'
import { Bunker } from '@/models/Bunker'
import { CustomLocation } from '@/models/CustomLocation'
import { GeoJSON } from 'geojson'
import CargoSizeQuantityUnit from '@/enums/CargoSizeQuantityUnit'
import BillOfLadingQuantityUnit from '@/enums/BillOfLadingQuantityUnit'
import TransportLocationQuantityUnit from '@/enums/TransportLocationQuantityUnit'
import TransportationType from '@/enums/TransportationType'
import DOMPurify from 'dompurify'

const TerminalStore = namespace('terminals')
const Auth = namespace('auth')

@Component({
	components: {
		FormValidationResults,
		DateTimePicker,
		Icon,
		SpecificVesselPicker,
		UnregisteredVesselCreator,
		LocationSelector
	}
})
export default class BillOfLadingForm extends Mixins(Testable) {
	@Prop({ required: true })
	type!: 'loading-bill' | 'unloading-bill'

	@Prop({ required: false, default: null })
	value!: BillOfLading | null

	@Prop({ required: false, default: null })
	transport!: Transport | null

	@Prop({ required: false, default: null })
	terminalVisit!: TerminalVisit | null

	@Prop({ required: false, default: null })
	cargo!: Cargo | null

	@Prop({ required: false, default: null })
	shipper!: Company | null

	@Prop({ required: false, default: null })
	defaultStartTerminal!: Terminal | null

	@Prop({ required: false, default: null })
	defaultDestinationTerminal!: Terminal | null

	@Prop({ required: false, default: null })
	loadingBill!: BillOfLading | null

	@Prop({ required: false, default: null })
	receiver!: Company | null

	user: User | null = null
	vessel: Vessel | null = null
	skipperName: string | null = null
	loadingPlace: GeoDirection | FacilityDirection | null = null
	unloadingPlace: GeoDirection | FacilityDirection | null = null
	startTerminal: Terminal | null = null
	destinationTerminal: Terminal | null = null
	customDestinationLocation: CustomLocation | null = null

	transportType: TransportType = TransportType.BULK

	isLoading: boolean = false
	submitted: boolean = false
	cargoTypesLoading: boolean = false
	certificationClaimTypes: Array<CertificationClaimType> = []
	frequentlyUsedExtractionAreas: Array<BillOfLading> = []
	frequentlyUsedExtractors: Array<BillOfLading> = []
	frequentlyUsedCargoTypes: Array<CargoType> = []
	extractionAreas: Array<Bunker> = []
	cargoTypes: Array<CargoType> = []
	deliveryMethods = [
		{
			value: DeliveryMethod.ON_BOARD,
			description: this.$i18n.t(
				'documentGenerator.billOfLading.delivery_method.' + DeliveryMethod.ON_BOARD
			) as string
		},
		{
			value: DeliveryMethod.ON_SHORE,
			description: this.$i18n.t(
				'documentGenerator.billOfLading.delivery_method.' + DeliveryMethod.ON_SHORE
			) as string
		},
		{
			value: DeliveryMethod.AT_WORK,
			description: this.$i18n.t(
				'documentGenerator.billOfLading.delivery_method.' + DeliveryMethod.AT_WORK
			) as string
		}
	]

	billOfLadings: Array<BillOfLading> = []
	recentBOL: BillOfLading | null = null

	openVesselCreator: boolean = false
	showLoadingExcerpt: boolean = false
	showCertificatesExcerpt: boolean = false

	extractor: string = ''
	loadingBillReceiptNumber: string = ''

	holdIsOpen: boolean = false
	holdDimensions: number = 0
	holdDimensionsUncovered: number = 0

	applicabilityClaim: CertificationClaimType | null = null
	cargoType: CargoType | string | null = null
	licenseHolder: string = ''
	deedNumber: string = ''
	certificateNumber: string = ''
	certificateHolder: string = ''
	certificateType: 'bsb' | 'benor' | 'no_bsb_benor' = 'bsb'
	certificateMarking: string | null = null
	certificationIsCE: boolean = false
	certificationIsDOP: boolean = false
	certificationCEMarking: string = ''
	certificationDOPMarking: string = ''
	certificationIsBRL: boolean = false
	certificationBRLMarking: string = ''
	skipperDeclareCleanHold: boolean = true
	certificationBenorRevision: string = ''
	certificationBenorCertificateNumber: number | null = null
	environmentalQuality: string = 'Voldoet aan achtergrondwaarden'
	issueLocation: string = ''
	generatedAt: { start: string; end: string } = {
		start: moment().format('YYYY-MM-DD'),
		end: ''
	}
	extractionArea: Bunker | { id: string; name: string } | string | null = null
	deliveryMethod: { value: string; description: string } | null = null
	loadingPeriodFrom: Moment = moment()
	loadingPeriodUntil: Moment = moment().add(1, 'hour')
	rinsePeriodFrom: Moment = moment()
	rinsePeriodUntil: Moment = moment()
	quantity: number | null = null
	quantityUnit: BillOfLadingQuantityUnit = BillOfLadingQuantityUnit.TON
	sandIsRinsed: boolean | null = null
	desalinationMethod: string = DesalinationMethod.SUBMERGED
	desalinationWaterUsed: number = 0
	measuredSaltContentFrontHopper: string = ''
	measuredSaltContentRearHopper: string = ''
	dryPumpUsed: boolean = false
	dryPumpHours: number | null = null
	remark: string = ''

	// Prevent race conditions when deliveryMethod changes rapidly
	private deliveryMethodRequestId: number = 0

	@TerminalStore.Mutation('cache')
	cacheTerminals

	@Auth.Getter('isBroker')
	isBroker

	@Auth.Getter('isShipper')
	isShipper

	@Auth.Getter('isSkipper')
	isSkipper

	get isLoadingBill() {
		return this.type === 'loading-bill'
	}

	get hideInformationForSkipper() {
		return this.isSkipper && !this.isLoadingBill
	}

	get isQuantityRequired(): boolean {
		if (this.isLoadingBill) return false
		if (this.user?.getCompany()?.getApiId() === this.transport?.getCompany()?.getApiId()) return false
		return true
	}

	get filteredCargoTypes(): Array<CargoType> {
		return this.frequentlyUsedCargoTypes
			.concat(this.cargoTypes)
			.filter((cargoType) => cargoType.bulkType !== 'liquid_bulk')
	}

	get hasDeliveryMethodOnBoard() {
		return this.deliveryMethod?.value === DeliveryMethod.ON_BOARD
	}

	get vesselExcerpt() {
		const vessel = this.terminalVisit?.getVessel() ?? this.transport?.getActiveVessel()
		const skipperName = this.skipperName ?? vessel?.skipperName
		return `${vessel?.name} (${vessel?.euNumber})` + (skipperName ? `, (${skipperName})` : '')
	}

	get canEditVessel() {
		return this.isBroker || this.isShipper
	}

	get allExtractionAreas(): Array<Bunker | { id: string; name: string }> {
		const frequentlyUsed = this.frequentlyUsedExtractionAreas
			.filter((area) => !!area.extractionArea)
			.map((area) => {
				return { id: area.extractionArea, name: area.extractionArea }
			})
		return [...this.extractionAreas, ...frequentlyUsed]
	}

	async created() {
		this.isLoading = true
		this.$validator.pause() // because some fields are conditionally shown the validator otherwise throws errors
		this.user = await User.fetchMeLazy()

		try {
			const cargoTypesQuery = CargoType.fetchAllLazy()
			const certificationClaimTypesQuery = CertificationClaimType.fetchAllLazy()

			this.cargoTypes = await cargoTypesQuery
			this.certificationClaimTypes = await certificationClaimTypesQuery

			if (this.isLoadingBill) {
				await this.setValuesForLoadingBill()
			} else {
				await this.setValuesForUnloadingBill()
			}

			if (this.defaultStartTerminal) {
				this.setLoadingPlaceAndStartTerminal(this.defaultStartTerminal)
			} else {
				this.setLoadingPlaceFromTransport()
			}
			if (this.defaultDestinationTerminal) {
				this.setUnloadingPlaceAndDestinationTerminal(this.defaultDestinationTerminal)
			} else {
				this.setUnloadingPlaceFromTransport()
			}

			this.frequentlyUsedCargoTypes = await User.fetchFrequentlyUsedCargoTypesLazy()

			if (!this.isLoadingBill || (this.isLoadingBill && this.hasDeliveryMethodOnBoard)) {
				this.setStateFromDeliveryTerms()
			} else if (this.isLoadingBill) {
				// only copy BRL marking from delivery terms
				this.certificationBRLMarking = this.cargo?.getDeliveryTerms()?.certificationBRLMarking ?? ''
			}
			this.setStateFromProps()
		} finally {
			this.isLoading = false
		}

		await this.$nextTick()
		this.$validator.resume()
	}

	private setStateFromDeliveryTerms() {
		const deliveryTerms = this.cargo?.getDeliveryTerms()
		if (deliveryTerms) {
			this.certificationIsCE = deliveryTerms.certificationIsCE ?? false
			this.certificationCEMarking = deliveryTerms.certificationCEMarking ?? ''
			this.certificateType = deliveryTerms.certificationIsBenor
				? 'benor'
				: deliveryTerms.certificationIsBSB
				  ? 'bsb'
				  : 'no_bsb_benor'
			this.certificateMarking =
				deliveryTerms.certificationBenorMarking ?? deliveryTerms.certificationBSBMarking ?? null
			this.certificationBenorRevision = deliveryTerms.certificationBenorRevision ?? ''
			this.certificationBenorCertificateNumber = deliveryTerms.certificationBenorCertificateNumber ?? null
			this.sandIsRinsed = deliveryTerms.isDesalinated ?? false
			this.dryPumpUsed = deliveryTerms.dryPumpUsed ?? false
			this.certificationIsDOP = deliveryTerms.certificationIsDOP ?? false
			this.certificationDOPMarking = deliveryTerms.certificationDOPMarking ?? ''
			this.certificationIsBRL = deliveryTerms.certificationIsBRL ?? false
			this.certificationBRLMarking = deliveryTerms.certificationBRLMarking ?? ''
			this.applicabilityClaim = deliveryTerms.getApplicabilityClaim()
		}
	}

	private setStateFromProps() {
		if (this.cargo) {
			this.transportType = this.cargo.transport
			this.cargoType = this.cargo.customProduct ?? this.cargo.getCargoType() ?? null

			const unloadingLocation = this.transport?.getLocations().find((loc) => loc.sequence === 1)
			if (!unloadingLocation?.dischargedQuantityUnit) {
				this.quantityUnit =
					this.cargo.cargoSizeUnit === CargoSizeQuantityUnit.PER_TON
						? BillOfLadingQuantityUnit.TON
						: BillOfLadingQuantityUnit.CUBIC
			}
		}

		if (!this.loadingBill) {
			if (this.terminalVisit) {
				this.vessel = this.terminalVisit.getVessel()
				this.cargoType = this.terminalVisit.getProduct()
				this.licenseHolder = this.terminalVisit.license
				this.quantityUnit =
					this.terminalVisit.quantityUnit === TransportLocationQuantityUnit.TON
						? BillOfLadingQuantityUnit.TON
						: BillOfLadingQuantityUnit.CUBIC
				this.extractionArea = this.terminalVisit.getExtractionArea() ?? this.extractionArea
			}
			if (this.transport) {
				this.vessel = this.transport.getActiveVessel()
			}

			this.certificateHolder = this.certificateHolder ?? this.shipper?.name ?? ''
			if (!this.isLoadingBill) {
				this.deliveryMethod = this.deliveryMethods[1]
			} else {
				this.deliveryMethod =
					this.deliveryMethods.find((method) => method.value === this.terminalVisit?.deliveryMethod) ??
					this.deliveryMethods[0] // Franco on board is 80% of the cases
			}
		}

		this.setHoldDimensionsFromVessel()

		this.issueLocation = this.shipper?.place ?? ''
		this.sandIsRinsed = this.cargo?.getDeliveryTerms()?.isDesalinated ?? false

		this.setDefaultsForLoadingBill()
	}

	private setHoldDimensionsFromVessel() {
		if (this.vessel) {
			this.holdDimensions = this.vessel.capacityCubicMeters ?? 0
			this.holdDimensionsUncovered = this.vessel.capacityCubicMetersUncovered ?? 0
		}
	}

	private setDefaultsForLoadingBill() {
		if (this.isLoadingBill && !this.hasDeliveryMethodOnBoard) {
			this.certificateType = 'bsb'
			if (this.sandIsRinsed) {
				this.applicabilityClaim = this.applicabilityClaims.find((claim) => claim.slug === 'limited') ?? null
				this.environmentalQuality = 'Voldoet aan achtergrondwaarden, met uitzondering van chloride.'
			}
		}
	}

	private async setValuesForLoadingBill() {
		this.quantityUnit = BillOfLadingQuantityUnit.CUBIC

		const lastLoadingBillQuery =
			this.user?.latestLoadingBill().where('transportationType', TransportationType.VESSEL).get() ??
			Promise.resolve(null as any)
		const lastLoadingBills = [] as Array<BillOfLading>
		const result = (await lastLoadingBillQuery).getData() as BillOfLading
		if (result) {
			lastLoadingBills.push(result)
		}
		this.frequentlyUsedExtractors = lastLoadingBills
		this.frequentlyUsedExtractionAreas = lastLoadingBills

		if (lastLoadingBills.length) {
			this.showLoadingExcerpt = this.showCertificatesExcerpt = true

			const latestStartTerminal = lastLoadingBills[0].getStartLocation()
			// cache the terminal to prevent extra query
			this.cacheTerminals([latestStartTerminal])
			if (latestStartTerminal) {
				this.setLoadingPlaceAndStartTerminal(latestStartTerminal)
			}
			this.extractor = lastLoadingBills[0].extractor
			const lastExtractionArea = lastLoadingBills[0].extractionArea
			if (lastExtractionArea && !this.extractionArea) {
				this.extractionArea = {
					id: lastExtractionArea,
					name: lastExtractionArea
				}
			}
			this.certificationIsDOP = lastLoadingBills[0].certificationIsDOP
			this.certificationDOPMarking = lastLoadingBills[0].certificationDOPMarking
			this.certificationIsCE = lastLoadingBills[0].certificationIsCE
			this.certificationCEMarking = lastLoadingBills[0].certificationCEMarking
			this.applicabilityClaim = lastLoadingBills[0].getApplicabilityClaim()
		} else {
			this.applicabilityClaim = this.applicabilityClaims.find((claim) => claim.slug === 'limited') ?? null
		}
	}

	private async setValuesForUnloadingBill() {
		this.showLoadingExcerpt = this.defaultStartTerminal !== null
		this.showCertificatesExcerpt = true

		if (this.loadingBill) {
			this.setValuesFromLoadingBill()
		} else if (this.cargo) {
			const billsOfFrequentExtractionAreasQuery = this.cargo.frequentlyUsedExtractionAreas().get()
			const frequentlyUsedExtractorsQuery = this.cargo.frequentlyUsedExtractors().get()
			const latestLoadingBillQuery = this.transport
				? this.transport?.latestLoadingBill().where('transportationType', TransportationType.VESSEL).get()
				: Promise.resolve(null as any)

			const billsOfFrequentExtractionAreas = (
				await billsOfFrequentExtractionAreasQuery
			).getData() as Array<BillOfLading>
			this.frequentlyUsedExtractionAreas = billsOfFrequentExtractionAreas
			this.frequentlyUsedExtractors = (await frequentlyUsedExtractorsQuery).getData() as Array<BillOfLading>
			if (billsOfFrequentExtractionAreas.length) {
				this.extractionArea = {
					id: billsOfFrequentExtractionAreas[0].extractionArea,
					name: billsOfFrequentExtractionAreas[0].extractionArea
				}
				this.licenseHolder = billsOfFrequentExtractionAreas[0].licenseHolder
				this.deedNumber = billsOfFrequentExtractionAreas[0].deedNumber
				this.certificateNumber = billsOfFrequentExtractionAreas[0].certificateNumber
				this.certificationIsDOP = billsOfFrequentExtractionAreas[0].certificationIsDOP
				this.certificationDOPMarking = billsOfFrequentExtractionAreas[0].certificationDOPMarking
			}
			const latestLoadingBill = (await latestLoadingBillQuery).getData() as BillOfLading
			if (latestLoadingBill) {
				this.extractor = latestLoadingBill.extractor
				this.extractionArea = {
					id: latestLoadingBill.extractionArea,
					name: latestLoadingBill.extractionArea
				}
				this.licenseHolder = latestLoadingBill.licenseHolder
				this.deedNumber = latestLoadingBill.deedNumber
				this.certificateNumber = latestLoadingBill.certificateNumber
				this.certificateHolder = latestLoadingBill.certificateHolder
				if (latestLoadingBill.getApplicabilityClaim()) {
					this.applicabilityClaim = await CertificationClaimType.fetchLazy(
						latestLoadingBill.getApplicabilityClaim().getApiId()
					)
				}
			}
		}

		// set quantity and unit from transport location
		const unloadingLocation = this.transport?.getLocations().find((loc) => loc.sequence === 1)
		if (unloadingLocation?.dischargedQuantity) {
			this.quantity = unloadingLocation.dischargedQuantity ?? null
			this.quantityUnit =
				unloadingLocation.dischargedQuantityUnit === TransportLocationQuantityUnit.TON
					? BillOfLadingQuantityUnit.TON
					: BillOfLadingQuantityUnit.CUBIC
		}

		this.dryPumpUsed = true
	}

	private setValuesFromLoadingBill() {
		if (!this.loadingBill) return
		this.extractor = this.loadingBill.extractor
		this.loadingBillReceiptNumber = this.loadingBill.receiptNumber
		this.extractionArea = {
			id: this.loadingBill.extractionArea,
			name: this.loadingBill.extractionArea
		}
		this.vessel = this.loadingBill.getActiveVessel()
		this.skipperName = this.loadingBill.skipperName
		this.deliveryMethod =
			this.deliveryMethods.find((method) => method.value === this.loadingBill!.deliveryMethod) ??
			this.deliveryMethods[1]
		this.cargoType = this.loadingBill.customProduct ?? this.loadingBill.getBillOfLadingProduct().getCargoType()
		this.certificateMarking =
			this.loadingBill.certificationBenorMarking ?? this.loadingBill.certificationBSBMarking ?? null
		this.certificationBenorRevision = this.loadingBill.certificationBenorRevision
		this.certificationBenorCertificateNumber = this.loadingBill.certificationBenorCertificateNumber ?? null
		this.certificateType = this.loadingBill.certificationIsBenor
			? 'benor'
			: this.loadingBill.certificationIsBSB
			  ? 'bsb'
			  : 'no_bsb_benor'
		this.certificationIsCE = this.loadingBill.certificationIsCE
		this.certificationIsDOP = this.loadingBill.certificationIsDOP
		this.certificationCEMarking = this.loadingBill.certificationCEMarking
		this.certificationDOPMarking = this.loadingBill.certificationDOPMarking
		this.quantity = this.loadingBill.getBillOfLadingProduct().quantity
		this.quantityUnit = this.loadingBill.getBillOfLadingProduct().quantityUnit
		this.applicabilityClaim = this.loadingBill.getApplicabilityClaim()
	}

	setLoadingPlaceAndStartTerminal(terminal: Terminal) {
		this.loadingPlace = new FacilityDirection()
		this.loadingPlace.facilityType = 'terminals'
		this.loadingPlace.facilityId = terminal.getApiId()!
		this.loadingPlace.text = terminal.name + ', ' + terminal.place
	}

	setUnloadingPlaceAndDestinationTerminal(terminal: Terminal) {
		this.unloadingPlace = new FacilityDirection()
		this.unloadingPlace.facilityType = 'terminals'
		this.unloadingPlace.facilityId = terminal.getApiId()!
		this.unloadingPlace.text = terminal.name + ', ' + terminal.place
	}

	setUnloadingPlaceFromTransport() {
		if (!this.cargo) {
			return
		}
		if (this.cargo.customDestinationPlace) {
			const direction = new GeoDirection()
			direction.geo = this.cargo.customDestinationGeo as GeoJSON.Point
			direction.country = this.cargo.customDestinationCountryCode ?? ''
			direction.text = this.cargo.customDestinationPlace ?? ''

			this.unloadingPlace = direction
		}
	}

	setLoadingPlaceFromTransport() {
		if (!this.cargo) {
			return
		}
		if (this.cargo.getStartTerminal()) {
			this.setLoadingPlaceAndStartTerminal(this.cargo.getStartTerminal())
		}
	}

	newLoadingFromDateSelected(newDate) {
		this.loadingPeriodFrom = newDate
		this.loadingPeriodUntil.year(newDate.year())
		this.loadingPeriodUntil.month(newDate.month())
		this.loadingPeriodUntil.day(newDate.day())
	}

	newLoadingUntilDateSelected(newDate) {
		this.loadingPeriodUntil = newDate
		this.loadingPeriodFrom.year(newDate.year())
		this.loadingPeriodFrom.month(newDate.month())
		this.loadingPeriodFrom.day(newDate.day())
	}

	newRinseFromDateSelected(newDate) {
		this.rinsePeriodFrom = newDate
		this.rinsePeriodUntil.year(newDate.year())
		this.rinsePeriodUntil.month(newDate.month())
		this.rinsePeriodUntil.day(newDate.day())
	}

	newRinseUntilDateSelected(newDate) {
		this.rinsePeriodUntil = newDate
		this.rinsePeriodFrom.year(newDate.year())
		this.rinsePeriodFrom.month(newDate.month())
		this.rinsePeriodFrom.day(newDate.day())
	}

	@Watch('rinsePeriodFrom')
	onRinsePeriodFromFromDateChange() {
		if (this.rinsePeriodFrom.isAfter(this.rinsePeriodUntil)) {
			this.rinsePeriodUntil = this.rinsePeriodFrom.clone().add(1, 'hour')
		}
	}

	@Watch('rinsePeriodUntil')
	onRinsePeriodUntilFromDateChange() {
		if (this.rinsePeriodFrom.isAfter(this.rinsePeriodUntil)) {
			this.rinsePeriodFrom = this.rinsePeriodUntil.clone().add(-1, 'hour')
		}
	}

	@Watch('loadingPeriodFrom')
	onLoadingPeriodFromFromDateChange() {
		if (this.loadingPeriodFrom.isAfter(this.loadingPeriodUntil)) {
			this.loadingPeriodUntil = this.loadingPeriodFrom.clone().add(1, 'hour')
		}
	}

	@Watch('loadingPeriodUntil')
	onLoadingPeriodUntilFromDateChange() {
		if (this.loadingPeriodFrom.isAfter(this.loadingPeriodUntil)) {
			this.loadingPeriodFrom = this.loadingPeriodUntil.clone().add(-1, 'hour')
		}
	}

	extractionAreasLoading = false
	isFrequentlyUsedLoading = false
	isFrequentlyUsedLoaded = false

	@Watch('startTerminal')
	async updateFrequentlyUsedValuesForLoadingBill() {
		if (this.isFrequentlyUsedLoading || this.isFrequentlyUsedLoaded) return
		if (!this.isLoadingBill || !this.cargo || !this.startTerminal) return

		this.isFrequentlyUsedLoading = true

		try {
			const frequentlyUsedExtractionAreasQuery = this.cargo.frequentlyUsedExtractionAreas().get()
			const frequentlyUsedExtractorsQuery = this.cargo.frequentlyUsedExtractors().get()

			const frequentlyUsedExtractionAreas = (
				await frequentlyUsedExtractionAreasQuery
			).getData() as Array<BillOfLading>
			this.frequentlyUsedExtractors = (await frequentlyUsedExtractorsQuery).getData() as Array<BillOfLading>
			this.frequentlyUsedExtractionAreas = frequentlyUsedExtractionAreas

			if (frequentlyUsedExtractionAreas.length) {
				const extractionArea = frequentlyUsedExtractionAreas[0].extractionArea
				if (extractionArea && !this.extractionArea) {
					this.extractionArea = { id: extractionArea, name: extractionArea }
				}
				this.licenseHolder = this.licenseHolder ?? frequentlyUsedExtractionAreas[0].licenseHolder
				this.deedNumber = this.deedNumber ?? frequentlyUsedExtractionAreas[0].deedNumber
				this.certificateNumber = this.certificateNumber ?? frequentlyUsedExtractionAreas[0].certificateNumber
			}
			this.isFrequentlyUsedLoaded = true
		} finally {
			this.isFrequentlyUsedLoading = false
		}
	}

	async getRecentLoadingBillByStartTerminal(
		isFOB?: boolean,
		isRinsed?: boolean | null
	): Promise<BillOfLading | null> {
		if (this.startTerminal?.getApiId()) {
			let recentQuery = this.user
				?.getCompany()
				?.latestLoadingBill()
				.where('transportationType', TransportationType.VESSEL)
				.where('startLocation', this.startTerminal.getApiId() as string)
			if (!recentQuery) return null
			if (this.transport?.getCargo()?.transportationType) {
				recentQuery = recentQuery.where('transportationType', this.transport?.getCargo()?.transportationType)
			}
			if (typeof isFOB === 'boolean') {
				recentQuery = recentQuery.where('isFOB', isFOB.toString())
			}
			if (typeof isRinsed === 'boolean') {
				recentQuery = recentQuery.where('sandIsRinsed', isRinsed.toString())
			}
			return (await recentQuery.get()).getData() as BillOfLading
		}
		return null
	}

	getExtractionAreaName(extractionArea: string | Bunker | { id: string; name: string } | null) {
		if (typeof extractionArea === 'string') {
			return extractionArea
		}
		return extractionArea?.name
	}

	@Watch('loadingPlace')
	async updateExtractionAreaFilteredByStartLocation(newVal) {
		this.startTerminal = await fetchWhenTerminal(newVal)
		if (this.startTerminal?.isManaged && !this.isSkipper) {
			this.extractionAreasLoading = true
			this.extractionAreas = (await this.startTerminal.extractionAreas().get()).getData() as Array<Bunker>
			this.extractionAreasLoading = false
		} else {
			this.extractionAreas = []
		}
	}

	@Watch('deliveryMethod')
	@Watch('startTerminal')
	async updateEnvironmentalQualityFromDeliveryMethod() {
		const requestId = ++this.deliveryMethodRequestId
		const startTerminalIdAtRequest = this.startTerminal?.getApiId()
		const recentBOL = await this.getRecentLoadingBillByStartTerminal(
			this.hasDeliveryMethodOnBoard,
			this.sandIsRinsed
		)
		// If another request started meanwhile, abort applying result to prevent race condition
		if (requestId !== this.deliveryMethodRequestId) return
		// If start terminal changed during the fetch, ignore stale result
		if (startTerminalIdAtRequest !== this.startTerminal?.getApiId()) return

		if (recentBOL?.environmentalQuality) {
			this.environmentalQuality = recentBOL.environmentalQuality
		}
		if (!this.terminalVisit && recentBOL?.extractionArea) {
			const recentExtractionArea = recentBOL.extractionArea
			this.extractionArea = this.extractionAreas.find((bunker) => bunker.name === recentExtractionArea) ?? {
				id: recentExtractionArea,
				name: recentExtractionArea
			}
		}
	}

	@Watch('unloadingPlace')
	async updateDestinationTerminal(newVal) {
		this.destinationTerminal = await fetchWhenTerminal(newVal)
	}

	@Watch('extractionArea')
	@Watch('extractionAreas')
	setCertificateFieldsFromExtractionArea() {
		const extractionArea: Bunker | { id: string; name: string } | string | null = this.extractionArea
		let found = extractionArea as Bunker | undefined
		if (!(extractionArea instanceof Bunker)) {
			found = this.extractionAreas.find((bunker) => bunker.name === this.getExtractionAreaName(extractionArea))
		}
		if (found) {
			this.deedNumber = found.deedNumber ?? this.deedNumber
			this.licenseHolder = found.licenseHolder ?? this.licenseHolder
			this.certificateHolder = found.certificationHolder ?? this.certificateHolder
			this.certificateNumber = found.certificationNumber ?? this.certificateNumber
		}
	}

	@Watch('cargo')
	setDefaultCargoFields() {
		if (!this.cargo) return
		this.transportType = this.cargo.transport
	}

	@Watch('vessel')
	changeVessel() {
		if (!this.vessel) return
		this.holdDimensions = this.vessel.capacityCubicMeters ?? this.holdDimensions
		this.holdDimensionsUncovered = this.vessel.capacityCubicMetersUncovered ?? this.holdDimensionsUncovered
		this.skipperName = this.loadingBill?.skipperName ?? this.vessel.skipperName ?? null
	}

	setCreatedVessel(unregisteredVessel) {
		this.vessel = unregisteredVessel
		this.openVesselCreator = false
	}

	get applicabilityClaims() {
		if (!this.certificationClaimTypes) return []
		return this.certificationClaimTypes.filter(
			(certificationClaimType) => certificationClaimType.claimType === ClaimType.BILL_OF_LADING_APPLICABILITY
		)
	}

	get isBenor() {
		return this.certificateType === 'benor'
	}

	get isBSB() {
		return this.certificateType === 'bsb'
	}

	get norBSBOrBenor() {
		return this.certificateType === 'no_bsb_benor'
	}

	get isDestinationValid() {
		return !!this.unloadingPlace
	}

	get cargoTypeEnabled() {
		if (!this.terminalVisit) return true
		return !this.terminalVisit.getProduct()
	}

	get selectedExtractionAreaBunker(): Bunker | null {
		return (
			this.extractionAreas.find((bunker) => bunker.name === this.getExtractionAreaName(this.extractionArea)) ??
			null
		)
	}

	get hasDeliveryTerms(): boolean {
		return this.isLoadingBill || !!this.cargo?.getDeliveryTerms()
	}

	async validate() {
		this.submitted = true
		const hasStartLocation = !!this.loadingPlace
		const destinationValid = this.isDestinationValid

		return (await this.$validator.validateAll()) && hasStartLocation && destinationValid
	}

	async submit(): Promise<BillOfLading> {
		const bil = this.value!

		bil.certificationIsBenor = this.isBenor
		bil.certificationIsBSB = this.isBSB
		bil.certificationBSBMarking = this.isBSB ? this.certificateMarking : null
		bil.certificationBenorMarking = this.isBenor ? this.certificateMarking : null
		bil.certificationBenorRevision = this.certificationBenorRevision
		bil.certificationBenorCertificateNumber = this.certificationBenorCertificateNumber ?? null
		bil.certificationIsCE = this.certificationIsCE
		bil.certificationIsDOP = this.certificationIsDOP
		bil.certificationCEMarking = this.certificationCEMarking
		bil.certificationDOPMarking = this.certificationDOPMarking
		bil.certificationIsBRL = this.certificationIsBRL
		bil.certificationBRLMarking = this.certificationBRLMarking
		bil.skipperDeclareCleanHold = this.skipperDeclareCleanHold

		bil.generatedAt = this.generatedAt.start
		bil.desalinationMethod = this.desalinationMethod
		bil.desalinationWaterUsedInCubicMeter = '' + this.desalinationWaterUsed
		bil.extractor = this.extractor
		bil.extractionArea =
			(typeof this.extractionArea === 'string' ? this.extractionArea : this.extractionArea?.name) ?? ''
		if (this.deliveryMethod) {
			bil.deliveryMethod = this.deliveryMethod.value
		}
		bil.environmentalQuality = this.environmentalQuality
		bil.holdIsOpen = this.holdIsOpen
		bil.holdDimensionsInCubicMeter = '' + (this.holdIsOpen ? this.holdDimensionsUncovered : this.holdDimensions)
		bil.issueLocation = this.issueLocation
		bil.loadingPeriodFrom = this.loadingPeriodFrom.format('Y-MM-DD HH:mm:ss')
		bil.loadingPeriodUntil = this.loadingPeriodUntil.format('Y-MM-DD HH:mm:ss')

		this.setValuesByDocumentType(bil)

		bil.remark = DOMPurify.sanitize(this.remark)
		bil.quantity = this.quantity || null // || on purpose as ?? keeps ''
		bil.quantityUnit = this.quantityUnit
		bil.skipperName = this.skipperName
		bil.loadingBillReceiptNumber = this.loadingBillReceiptNumber

		if (this.transport) {
			bil.setTransport(this.transport)
		}
		if (this.terminalVisit) {
			bil.setTerminalVisit(this.terminalVisit)
		}
		if (this.vessel) {
			if (this.vessel.isRegistered) {
				bil.setVessel(this.vessel)
			} else {
				bil.setUnregisteredVessel(this.vessel)
			}
		}
		if (this.receiver) {
			bil.setReceiver(this.receiver)
		}

		bil.setApplicabilityClaim(this.applicabilityClaim!)
		if (typeof this.cargoType === 'string') {
			bil.customProduct = this.cargoType
		} else if (this.cargoType instanceof CargoType) {
			bil.setCargoType(this.cargoType!)
		}
		bil.setStartLocation(this.startTerminal!)

		this.setValuesForUnloadingPlace(bil)

		await bil.save()

		return bil
	}

	setValuesForUnloadingPlace(bil: BillOfLading) {
		if (this.unloadingPlace instanceof GeoDirection) {
			bil.setCustomDestinationLocation(this.customDestinationLocation)
			bil.customDestinationLocationPlace = this.unloadingPlace.text
			bil.customDestinationLocationCountryCode = this.unloadingPlace.country
			if (typeof this.unloadingPlace.geo === 'string') {
				bil.customDestinationLocationGeo = this.unloadingPlace.geo
			} else {
				bil.customDestinationLocationGeo = `POINT(${this.unloadingPlace.geo.coordinates[0]} ${this.unloadingPlace.geo.coordinates[1]})`
			}
		} else {
			bil.setEndLocation(this.destinationTerminal!)
		}
	}

	setValuesByDocumentType(bil: BillOfLading) {
		bil.documentType = this.type === 'loading-bill' ? 'loading' : 'unloading'

		if (bil.documentType === 'unloading') {
			bil.measuredSaltContentFrontHopper =
				this.measuredSaltContentFrontHopper === ''
					? null
					: Number.parseFloat(this.measuredSaltContentFrontHopper)
			bil.measuredSaltContentRearHopper =
				this.measuredSaltContentRearHopper === '' ? null : Number.parseFloat(this.measuredSaltContentRearHopper)
			bil.sandIsRinsed = this.sandIsRinsed
			bil.sandRinsedFrom = this.rinsePeriodFrom.format('Y-MM-DD HH:mm:ss')
			bil.sandRinsedUntil = this.rinsePeriodUntil.format('Y-MM-DD HH:mm:ss')
			bil.dryPumpUsed = this.dryPumpUsed
			bil.dryPumpHours = this.dryPumpHours
		} else {
			bil.measuredSaltContentFrontHopper = null
			bil.measuredSaltContentRearHopper = null
			bil.sandIsRinsed = null
			bil.sandRinsedFrom = ''
			bil.sandRinsedUntil = ''
			bil.dryPumpUsed = null
			bil.dryPumpHours = null
			bil.certificateNumber = this.certificateNumber
			bil.certificateHolder = this.certificateHolder
			bil.deedNumber = this.deedNumber
			bil.licenseHolder = this.licenseHolder
		}
	}
}
